import type { FieldDto } from "../../types/field";
import { EMPTY_FIO, type FioParts } from "../../types/fio";
import { isFieldOptionsObject } from "./fieldOptions";
import { parseBoolean } from "./parseBoolean";

export function isPatronymicRequired(field: FieldDto): boolean {
  if (field.fio != null && parseBoolean(field.fio.patronymic_required)) {
    return true;
  }

  const options = field.options;
  if (options != null && isFieldOptionsObject(options)) {
    return parseBoolean(options.patronymic_required);
  }

  return false;
}

export function encodeFio(parts: FioParts): string {
  return JSON.stringify({
    last_name: parts.last_name.trim(),
    first_name: parts.first_name.trim(),
    patronymic: parts.patronymic.trim(),
  });
}

export function parseFio(raw: string | undefined): FioParts {
  if (!raw) return { ...EMPTY_FIO };

  try {
    const parsed = JSON.parse(raw) as Partial<FioParts>;
    return {
      last_name: String(parsed.last_name ?? "").trim(),
      first_name: String(parsed.first_name ?? "").trim(),
      patronymic: String(parsed.patronymic ?? "").trim(),
    };
  } catch {
    return { ...EMPTY_FIO };
  }
}

export function isFioEmpty(parts: FioParts): boolean {
  return !parts.last_name && !parts.first_name && !parts.patronymic;
}

/** Одна строка «Фамилия Имя Отчество» → части для API. */
export function parseFioLine(line: string): FioParts {
  const tokens = line.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { ...EMPTY_FIO };
  if (tokens.length === 1) {
    return { last_name: tokens[0], first_name: "", patronymic: "" };
  }
  if (tokens.length === 2) {
    return { last_name: tokens[0], first_name: tokens[1], patronymic: "" };
  }
  return {
    last_name: tokens[0],
    first_name: tokens[1],
    patronymic: tokens.slice(2).join(" "),
  };
}

export function formatFioLine(parts: FioParts): string {
  return [parts.last_name, parts.first_name, parts.patronymic]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");
}

export type FioFieldErrors = {
  last_name?: string;
  first_name?: string;
  patronymic?: string;
  general?: string;
};

export function validateFioField(
  field: Pick<FieldDto, "required">,
  parts: FioParts,
  patronymicRequired: boolean,
  isVisible = true,
): FioFieldErrors {
  if (!isVisible) return {};

  if (isFioEmpty(parts)) {
    if (!field.required) return {};
    return {
      general: patronymicRequired
        ? "Укажите фамилию, имя и отчество"
        : "Укажите фамилию и имя",
    };
  }

  const errors: FioFieldErrors = {};

  if (field.required || !isFioEmpty(parts)) {
    if (!parts.last_name.trim()) errors.last_name = "Укажите фамилию";
    if (!parts.first_name.trim()) errors.first_name = "Укажите имя";
  }

  if (patronymicRequired && !parts.patronymic.trim()) {
    errors.patronymic = "Укажите отчество";
  }

  return errors;
}

export function fioErrorsToList(errors: FioFieldErrors): string[] {
  const list: string[] = [];
  if (errors.general) list.push(errors.general);
  if (errors.last_name) list.push(errors.last_name);
  if (errors.first_name) list.push(errors.first_name);
  if (errors.patronymic) list.push(errors.patronymic);
  return list;
}

export function hasFioErrors(errors: FioFieldErrors): boolean {
  return Object.keys(errors).length > 0;
}
